import { Owner, Pet, Appointment, Examination, Vaccination } from '../types';
import { createClient } from '@supabase/supabase-js';

// Supabase konfigürasyonu
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-anon-key-here';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

/** Netlify / local: gerçek proje URL ve anon key yoksa Auth isteği boşa gider, doğrudan yedek giriş kullanılır */
export const isSupabaseAuthConfigured = (): boolean => {
  const url = process.env.REACT_APP_SUPABASE_URL || '';
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  return Boolean(
    url && key && !url.includes('your-project') && key !== 'your-anon-key-here'
  );
};

/**
 * true = URL/key yok veya placeholder → demo: okumada mock, yazmada localStorage.
 * false = canlı Supabase → yalnızca veritabanı; hata olursa fırlatılır (kalıcı kayıt tutarlılığı).
 */
export const allowOfflineFallback = (): boolean => !isSupabaseAuthConfigured();

// Mock Owners
export const mockOwners: Owner[] = [
  {
    id: '1',
    name: 'Feza Borak',
    phone: '0532 123 45 67',
    email: 'feza@example.com',
    address: 'İstanbul, Türkiye',
    createdAt: '2024-01-01'
  },
  {
    id: '2',
    name: 'Mehmet Uygun',
    phone: '0533 987 65 43',
    email: 'mehmet@example.com',
    address: 'Ankara, Türkiye',
    createdAt: '2024-01-02'
  },
  {
    id: '3',
    name: 'Mustafa Borak',
    phone: '0534 555 44 33',
    email: 'mustafa@example.com',
    address: 'İzmir, Türkiye',
    createdAt: '2024-01-03'
  }
];

// Mock Pets
export const mockPets: Pet[] = [
  {
    id: '1',
    name: 'mustafa',
    species: 'dog',
    breed: 'Golden Retriever',
    age: 3,
    weight: 25,
    gender: 'male',
    color: 'Altın',
    owner_id: '1',
    protocolNo: 'P001',
    neutered: true,
    lastVisitDate: '2024-08-03',
    createdAt: '2024-01-01'
  },
  {
    id: '2',
    name: 'Miay',
    species: 'cat',
    breed: 'British Shorthair',
    age: 2,
    weight: 4,
    gender: 'female',
    color: 'Gri',
    owner_id: '2',
    protocolNo: 'P002',
    neutered: false,
    lastVisitDate: '2024-08-02',
    createdAt: '2024-01-02'
  },
  {
    id: '3',
    name: 'Miyav',
    species: 'cat',
    breed: 'Tekir',
    age: 1,
    weight: 3.5,
    gender: 'male',
    color: 'Tekir',
    owner_id: '3',
    protocolNo: 'P003',
    neutered: true,
    lastVisitDate: '2024-07-28',
    createdAt: '2024-01-03'
  },
  {
    id: '4',
    name: 'Pamuk',
    species: 'dog',
    breed: 'Golden Retriever',
    age: 4,
    weight: 28,
    gender: 'female',
    color: 'Beyaz',
    owner_id: '1',
    protocolNo: 'P004',
    neutered: true,
    lastVisitDate: '2024-07-01',
    createdAt: '2024-01-04'
  }
];

// Mock Appointments
export const mockAppointments: Appointment[] = [
  {
    id: '1',
    pet_id: '1',
    date: '2024-08-05',
    time: '10:00',
    type: 'vaccination',
    notes: 'Karma aşı randevusu',
    status: 'scheduled',
    createdAt: '2024-08-01'
  },
  {
    id: '2',
    pet_id: '2',
    date: '2024-08-06',
    time: '14:30',
    type: 'examination',
    notes: 'Rutin kontrol',
    status: 'scheduled',
    createdAt: '2024-08-02'
  }
];

// Mock Examinations
export const mockExaminations: Examination[] = [
  {
    id: '1',
    pet_id: '1',
    date: '2024-08-03',
    symptoms: 'Halsizlik, iştahsızlık',
    diagnosis: 'Üst solunum yolu enfeksiyonu',
    treatment: 'Antibiyotik tedavisi',
    prescription: 'Amoxicillin 500mg 2x1',
    notes: 'Hasta iyileşme gösteriyor',
    anamnez: '3 gündür halsiz ve iştahsız',
    weight: 25.5,
    heartRate: 'Normal',
    temperature: '38.5°C',
    respiration: 'Normal',
    mucosa: 'Pembe',
    lymphNodes: 'Normal',
    dehydration: 'Yok',
    findings: 'Burun akıntısı, hafif öksürük',
    applications: 'Antibiyotik enjeksiyonu',
    otherPhysical: 'Genel durum iyi',
    createdAt: '2024-08-03'
  },
  {
    id: '2',
    pet_id: '2',
    date: '2024-08-02',
    symptoms: 'Kusma',
    diagnosis: 'Mide rahatsızlığı',
    treatment: 'Sindirim sistemi ilacı',
    prescription: 'Metoclopramide 10mg 3x1',
    notes: '24 saat açlık önerildi',
    anamnez: 'Dün gece yemek yedikten sonra kusma başladı',
    weight: 4.2,
    heartRate: 'Normal',
    temperature: '38.2°C',
    respiration: 'Normal',
    mucosa: 'Pembe',
    lymphNodes: 'Normal',
    dehydration: 'Hafif',
    findings: 'Mide hassasiyeti',
    applications: 'Sindirim sistemi ilacı',
    otherPhysical: 'Genel durum stabil',
    createdAt: '2024-08-02'
  }
];

// Mock Vaccinations
export const mockVaccinations: Vaccination[] = [
  {
    id: '1',
    pet_id: '1',
    name: 'Karma',
    date: '2024-08-03',
    next_due_date: '2024-08-04',
    status: 'completed',
    notes: 'Rutin aşı',
    vaccine_name: 'Karma Aşı',
    createdAt: '2024-08-03'
  },
  {
    id: '2',
    pet_id: '2',
    name: 'Karma',
    date: '2024-08-02',
    next_due_date: '2024-08-23',
    status: 'completed',
    notes: 'Yıllık aşı',
    vaccine_name: 'Karma Aşı',
    createdAt: '2024-08-02'
  },
  {
    id: '3',
    pet_id: '3',
    name: 'İç-dış parazit',
    date: '2024-07-28',
    next_due_date: '2024-09-28',
    status: 'completed',
    notes: 'Parazit tedavisi',
    vaccine_name: 'İç-dış Parazit İlacı',
    createdAt: '2024-07-28'
  },
  {
    id: '4',
    pet_id: '4',
    name: 'Kuduz Aşısı',
    date: '2024-07-01',
    next_due_date: '2024-07-01',
    status: 'completed',
    notes: 'Yıllık rutin aşı',
    vaccine_name: 'Kuduz Aşısı',
    createdAt: '2024-07-01'
  }
];

// Helper functions to get related data
export const getPetWithOwner = (petId: string): Pet | undefined => {
  const pet = mockPets.find(p => p.id === petId);
  if (pet) {
    pet.owner = mockOwners.find(o => o.id === pet.owner_id);
  }
  return pet;
};

export const getAppointmentWithPet = (appointmentId: string): Appointment | undefined => {
  const appointment = mockAppointments.find(a => a.id === appointmentId);
  if (appointment) {
    appointment.pet = getPetWithOwner(appointment.pet_id || appointment.petId || '');
  }
  return appointment;
};

export const getExaminationWithPet = (examinationId: string): Examination | undefined => {
  const examination = mockExaminations.find(e => e.id === examinationId);
  if (examination) {
    examination.pet = getPetWithOwner(examination.pet_id || examination.petId || '');
  }
  return examination;
};

export const getVaccinationWithPet = (vaccinationId: string): Vaccination | undefined => {
  const vaccination = mockVaccinations.find(v => v.id === vaccinationId);
  if (vaccination) {
    vaccination.pet = getPetWithOwner(vaccination.pet_id || vaccination.petId || '');
  }
  return vaccination;
}; 